/**
 * Copyright 2018 The AMP HTML Authors. All Rights Reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS-IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { base64UrlDecodeFromString } from './base64';

/** @const {string} */
const DELIMITER = '*';
const KEY_VALIDATOR = /^[a-zA-Z0-9\-_.]+$/;
const VALID_VERSION = 1;
const TAG = 'amp-analytics/linker';

function decode(value: string): string {
  return base64UrlDecodeFromString(String(value));
}

/**
 * Deserialize the serializedIds and return keyValue pairs.
 */
function deserialize(serializedIds: string): Record<string, string> {
  const keyValuePairs: Record<string, string> = {};
  const params = serializedIds.split(DELIMITER);
  for (let i = 0; i < params.length; i += 2) {
    const key = params[i];
    const valid = KEY_VALIDATOR.test(key);
    if (!valid) {
      console.error(TAG, `Invalid linker key ${key}, value ignored`);
      // eslint-disable-next-line no-continue
      continue;
    }
    const value = decode(params[i + 1]);
    keyValuePairs[key] = value;
  }
  return keyValuePairs;
}

/**
 * Parse the linker param value to version checksum and serializedParams
 */
function parseLinkerParamValue(value: string): {
  checksum?: string;
  serializedIds: string;
} | null {
  const parts = value.split(DELIMITER);
  const isEven = parts.length % 2 === 0;

  if (parts.length < 4 || !isEven) {
    // Format <version>*<checksum>*<key1>*<value1>
    // Note: linker makes sure there's at least one pair of non empty key value
    // Make sure there is at least three delimiters.
    console.error(TAG, `Invalid linker_param value ${value}`);
    return null;
  }

  const version = Number(parts.shift());
  if (version !== VALID_VERSION) {
    console.error(TAG, `Invalid version number ${version}`);
    return null;
  }

  const checksum = parts.shift();
  const serializedIds = parts.join(DELIMITER);
  return {
    checksum,
    serializedIds,
  };
}

/**
 * Return the key value pairs
 */
export function parseLinker(value: string): Record<string, string> | null {
  const linkerObj = parseLinkerParamValue(value);
  if (!linkerObj) {
    return null;
  }
  const { serializedIds } = linkerObj;
  return deserialize(serializedIds);
}
